import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import UpdateIcon from '@mui/icons-material/Update';
import TerminalIcon from '@mui/icons-material/Terminal';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LineChart } from '@mui/x-charts/LineChart';
import {
  fetchSystemStats,
  fetchResourceStats,
  fetchHardwareStats,
  fetchSysstatHistory,
  fetchUpdates,
  fetchSshSessions,
  type DiskStat,
  type SysstatSample,
} from '../api/stats';
import { fetchServices } from '../api/services';
import { fetchCrashSummary } from '../api/gameCrashes';
import { DiskBreakdownDrawer } from '../components/dashboard/DiskBreakdownDrawer';
import { SystemUpdateDialog } from '../components/dashboard/SystemUpdateDialog';
import { usePermission } from '../hooks/usePermission';
import { getSocket } from '../api/socket';
import type { ResourceStats } from '../api/stats';
import { Link as RouterLink } from 'react-router-dom';

// ── Formatters ─────────────────────────────────────────────────────────────

function fmtBytes(b: number): string {
  if (b >= 1_073_741_824) return `${(b / 1_073_741_824).toFixed(1)} GB`;
  if (b >= 1_048_576) return `${(b / 1_048_576).toFixed(0)} MB`;
  return `${(b / 1024).toFixed(0)} KB`;
}

function fmtUptime(s: number): string {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ── Shared sub-components ──────────────────────────────────────────────────

function StatRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <TableRow sx={{ '& td': { border: 0, py: 0.5, px: 0 } }}>
      <TableCell sx={{ color: 'text.secondary', width: '45%', pr: 2 }}>
        <Typography variant="caption">{label}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{value}</Typography>
      </TableCell>
    </TableRow>
  );
}

function UsageBar({
  label,
  value,
  total,
  percent,
  warn,
  crit,
}: {
  label: string;
  value: string;
  total: string;
  percent: number;
  warn?: boolean;
  crit?: boolean;
}) {
  const barColor = crit ? 'error.main' : warn ? 'warning.main' : 'primary.main';
  const pct = Math.min(100, Math.max(0, percent));
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2">
          {value} / {total}{' '}
          <Typography component="span" variant="caption" color="text.secondary">
            ({pct.toFixed(1)}%)
          </Typography>
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 1,
          backgroundColor: 'action.disabledBackground',
          '& .MuiLinearProgress-bar': { backgroundColor: barColor },
        }}
      />
    </Box>
  );
}

// ── Collecting-data placeholder ────────────────────────────────────────────

function CollectingPlaceholder({ height }: { height: number }) {
  return (
    <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography color="text.secondary" variant="body2">
        Collecting data… (first sample appears ~5 min after install)
      </Typography>
    </Box>
  );
}

// ── Dashboard ──────────────────────────────────────────────────────────────

export function Dashboard() {
  const qc = useQueryClient();
  const [breakdownMount, setBreakdownMount] = useState<string | null>(null);
  const [updateOpen, setUpdateOpen] = useState(false);
  const canReadStats = usePermission('stats.read');
  const canUpdate = usePermission('system.update');

  useEffect(() => {
    if (!canReadStats) return;
    const socket = getSocket();
    const onResources = (data: ResourceStats) => {
      qc.setQueryData(['stats/resources'], data);
    };
    socket.on('stats:resources', onResources);
    return () => { socket.off('stats:resources', onResources); };
  }, [canReadStats, qc]);

  const system = useQuery({
    queryKey: ['stats/system'],
    queryFn: fetchSystemStats,
    refetchInterval: 30_000,
  });

  const resources = useQuery({
    queryKey: ['stats/resources'],
    queryFn: fetchResourceStats,
    refetchInterval: 30_000,
  });

  const hardware = useQuery({
    queryKey: ['stats/hardware'],
    queryFn: fetchHardwareStats,
    refetchInterval: 60_000,
  });

  const sysstat = useQuery({
    queryKey: ['stats/sysstat-history'],
    queryFn: fetchSysstatHistory,
    refetchInterval: 5 * 60_000,
    staleTime: 4 * 60_000,
  });

  const updates = useQuery({
    queryKey: ['stats/updates'],
    queryFn: fetchUpdates,
    refetchInterval: 60_000,
  });

  const ssh = useQuery({
    queryKey: ['stats/ssh-sessions'],
    queryFn: fetchSshSessions,
    refetchInterval: 30_000,
  });

  const canReadServices = usePermission('services.read');
  const services = useQuery({
    queryKey: ['services'],
    queryFn: fetchServices,
    enabled: canReadServices,
    refetchInterval: 30_000,
  });

  const failedServices = (services.data?.services ?? []).filter((s) => s.active === 'failed');

  const canReadGames = usePermission('games.read');
  const crashSummary = useQuery({
    queryKey: ['crash-summary'],
    queryFn: fetchCrashSummary,
    enabled: canReadGames,
    staleTime: 2 * 60_000,
    refetchInterval: 5 * 60_000,
  });
  const gamesWithCrashes = (crashSummary.data?.summary ?? []).filter((s) => s.count_24h > 0);

  const syssamples: SysstatSample[] = sysstat.data?.samples ?? [];
  const hasSysstat = syssamples.length >= 2;

  const chartTimes = hasSysstat ? syssamples.map((s) => new Date(s.timestamp)) : [];
  const timeFmt = (v: Date) => v.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>Dashboard</Typography>

      {/* Alerts */}
      {failedServices.length > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" component={RouterLink} to="/services">
              View
            </Button>
          }
        >
          {failedServices.length === 1
            ? `Service failed: ${failedServices[0].name}`
            : `${failedServices.length} services failed: ${failedServices.map((s) => s.name).join(', ')}`}
        </Alert>
      )}
      {updates.data?.reboot_required && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Reboot required to apply updates.
        </Alert>
      )}
      {updates.data && updates.data.count > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 1 }}
          action={canUpdate ? (
            <Button color="inherit" size="small" onClick={() => setUpdateOpen(true)}>
              Apply
            </Button>
          ) : undefined}
        >
          {updates.data.count} package{updates.data.count !== 1 ? 's' : ''} available for upgrade.
        </Alert>
      )}
      {gamesWithCrashes.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" component={RouterLink} to="/games">
              View
            </Button>
          }
        >
          {gamesWithCrashes.length === 1
            ? `Game server "${gamesWithCrashes[0].game_id}" had crashes in the last 24h.`
            : `${gamesWithCrashes.length} game servers had crashes in the last 24h.`}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* System info */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              avatar={<StorageIcon color="primary" />}
              title="System"
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent sx={{ pt: 0 }}>
              {system.isLoading && <Skeleton variant="rectangular" height={120} />}
              {system.data && (
                <Table size="small">
                  <TableBody>
                    <StatRow label="Hostname" value={system.data.hostname} />
                    <StatRow label="OS" value={`${system.data.distro} ${system.data.release}`} />
                    <StatRow label="Kernel" value={system.data.kernel} />
                    <StatRow label="Arch" value={system.data.arch} />
                    <StatRow label="Uptime" value={fmtUptime(system.data.uptime_seconds)} />
                    <StatRow label="Timezone" value={system.data.timezone} />
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Resources */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              avatar={<MemoryIcon color="primary" />}
              title="Resources"
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent sx={{ pt: 0 }}>
              {resources.isLoading && <Skeleton variant="rectangular" height={160} />}
              {resources.data && (
                <>
                  <UsageBar
                    label="CPU"
                    value={`${resources.data.cpu_percent.toFixed(1)}%`}
                    total="100%"
                    percent={resources.data.cpu_percent}
                    warn={resources.data.cpu_percent >= 85}
                    crit={resources.data.cpu_percent >= 95}
                  />
                  <UsageBar
                    label="RAM"
                    value={fmtBytes(resources.data.ram.used)}
                    total={fmtBytes(resources.data.ram.total)}
                    percent={resources.data.ram.percent}
                    warn={resources.data.ram.percent >= 85}
                    crit={resources.data.ram.percent >= 95}
                  />
                  {resources.data.swap.total > 0 && (
                    <UsageBar
                      label="Swap"
                      value={fmtBytes(resources.data.swap.used)}
                      total={fmtBytes(resources.data.swap.total)}
                      percent={resources.data.swap.percent}
                      warn={resources.data.swap.percent >= 50}
                      crit={resources.data.swap.percent >= 80}
                    />
                  )}
                  {resources.data.disks.map((d: DiskStat) => (
                    <Box key={d.mount} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <UsageBar
                          label={`Disk ${d.mount}`}
                          value={fmtBytes(d.used)}
                          total={fmtBytes(d.total)}
                          percent={d.percent}
                          warn={d.warn}
                          crit={d.crit}
                        />
                      </Box>
                      <Tooltip title="Browse disk usage">
                        <IconButton
                          size="small"
                          sx={{ mt: 0.25, flexShrink: 0 }}
                          onClick={() => setBreakdownMount(d.mount)}
                        >
                          <FolderOpenIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Network Traffic chart — data from sysstat-history.json */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              avatar={<NetworkCheckIcon color="primary" />}
              title="Network Traffic — last 12 h"
              titleTypographyProps={{ variant: 'h6' }}
              subheader={hasSysstat ? `${syssamples.length} samples · 5-min intervals` : undefined}
            />
            <CardContent sx={{ pt: 0 }}>
              {sysstat.isLoading && <Skeleton variant="rectangular" height={200} />}
              {!sysstat.isLoading && !hasSysstat && <CollectingPlaceholder height={200} />}
              {hasSysstat && (
                <LineChart
                  xAxis={[{ data: chartTimes, scaleType: 'time', valueFormatter: timeFmt }]}
                  series={[
                    {
                      data: syssamples.map((s) => s.network.reduce((sum, n) => sum + n.rxKBs, 0)),
                      label: '↓ RX KB/s',
                      color: '#42a5f5',
                      area: true,
                      showMark: false,
                    },
                    {
                      data: syssamples.map((s) => s.network.reduce((sum, n) => sum + n.txKBs, 0)),
                      label: '↑ TX KB/s',
                      color: '#ef5350',
                      area: true,
                      showMark: false,
                    },
                  ]}
                  height={200}
                  margin={{ left: 50, right: 16, top: 16, bottom: 30 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* SSH sessions */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              avatar={<TerminalIcon color="primary" />}
              title="SSH Sessions"
              titleTypographyProps={{ variant: 'h6' }}
              action={
                ssh.data && (
                  <Chip
                    label={ssh.data.sessions.length}
                    size="small"
                    color={ssh.data.sessions.length > 0 ? 'warning' : 'default'}
                  />
                )
              }
            />
            <CardContent sx={{ pt: 0 }}>
              {ssh.isLoading && <Skeleton variant="rectangular" height={100} />}
              {ssh.data && ssh.data.sessions.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No active sessions
                </Typography>
              )}
              {ssh.data && ssh.data.sessions.length > 0 && (
                <Table size="small">
                  <TableBody>
                    {ssh.data.sessions.map((s, i) => (
                      <TableRow key={i} sx={{ '& td': { border: 0, py: 0.5, px: 0 } }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{s.user}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {s.from ?? s.tty}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color="text.secondary">
                            {s.login_time}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* CPU Usage chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<MemoryIcon color="primary" />}
              title="CPU Usage — last 12 h"
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent sx={{ pt: 0 }}>
              {sysstat.isLoading && <Skeleton variant="rectangular" height={180} />}
              {!sysstat.isLoading && !hasSysstat && <CollectingPlaceholder height={180} />}
              {hasSysstat && (
                <LineChart
                  xAxis={[{ data: chartTimes, scaleType: 'time', valueFormatter: timeFmt }]}
                  yAxis={[{ min: 0, max: 100, valueFormatter: (v: number) => `${v}%` }]}
                  series={[
                    {
                      data: syssamples.map((s) => s.cpu?.totalUsagePct ?? 0),
                      label: 'Total %',
                      color: '#ef5350',
                      area: true,
                      showMark: false,
                    },
                    {
                      data: syssamples.map((s) => s.cpu?.iowaitPct ?? 0),
                      label: 'I/O Wait %',
                      color: '#ff9800',
                      area: false,
                      showMark: false,
                    },
                  ]}
                  height={180}
                  margin={{ left: 50, right: 16, top: 16, bottom: 30 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Disk I/O chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<StorageIcon color="primary" />}
              title="Disk I/O — last 12 h"
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent sx={{ pt: 0 }}>
              {sysstat.isLoading && <Skeleton variant="rectangular" height={180} />}
              {!sysstat.isLoading && !hasSysstat && <CollectingPlaceholder height={180} />}
              {hasSysstat && (
                <LineChart
                  xAxis={[{ data: chartTimes, scaleType: 'time', valueFormatter: timeFmt }]}
                  yAxis={[{ valueFormatter: (v: number) => `${v} KB/s` }]}
                  series={[
                    {
                      data: syssamples.map((s) => s.disk?.readKBs ?? 0),
                      label: '↑ Read KB/s',
                      color: '#66bb6a',
                      area: true,
                      showMark: false,
                    },
                    {
                      data: syssamples.map((s) => s.disk?.writeKBs ?? 0),
                      label: '↓ Write KB/s',
                      color: '#ab47bc',
                      area: false,
                      showMark: false,
                    },
                  ]}
                  height={180}
                  margin={{ left: 60, right: 16, top: 16, bottom: 30 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Load Average mini-card */}
        {hasSysstat && (() => {
          const last = syssamples[syssamples.length - 1];
          const la = last?.loadAvg;
          if (!la) return null;
          const loadColor = (v: number | null) =>
            v === null ? 'text.secondary'
            : v >= 4   ? 'error.main'
            : v >= 2   ? 'warning.main'
            : 'success.main';
          return (
            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader
                  avatar={<ShowChartIcon color="primary" />}
                  title="Load Average"
                  titleTypographyProps={{ variant: 'h6' }}
                  subheader="1 min · 5 min · 15 min"
                />
                <CardContent>
                  <Stack direction="row" spacing={3} justifyContent="center">
                    {([['1m', la.load1], ['5m', la.load5], ['15m', la.load15]] as const).map(([label, value]) => (
                      <Box key={label} textAlign="center">
                        <Typography variant="h5" sx={{ color: loadColor(value) }}>
                          {value?.toFixed(2) ?? '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })()}

        {/* Memory Snapshot mini-card */}
        {hasSysstat && (() => {
          const last = syssamples[syssamples.length - 1];
          const mem = last?.memory;
          if (!mem) return null;
          const memColor = mem.usedPct >= 90 ? 'error.main' : mem.usedPct >= 75 ? 'warning.main' : 'success.main';
          const swapColor = mem.swapUsedMB > 512 ? 'warning.main' : 'text.primary';
          return (
            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader
                  avatar={<MemoryIcon color="primary" />}
                  title="Memory"
                  titleTypographyProps={{ variant: 'h6' }}
                  subheader={`${mem.usedMB} MB / ${mem.totalMB} MB`}
                />
                <CardContent>
                  <Stack direction="row" spacing={3} justifyContent="center">
                    <Box textAlign="center">
                      <Typography variant="h5" sx={{ color: memColor }}>
                        {mem.usedPct.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">used</Typography>
                    </Box>
                    {mem.swapUsedMB > 0 && (
                      <Box textAlign="center">
                        <Typography variant="h5" sx={{ color: swapColor }}>
                          {mem.swapUsedMB} MB
                        </Typography>
                        <Typography variant="caption" color="text.secondary">swap</Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })()}

        {/* Disk Utilization mini-card */}
        {hasSysstat && (() => {
          const last = syssamples[syssamples.length - 1];
          const disk = last?.disk;
          if (!disk) return null;
          const utilColor = disk.avgUtilPct >= 90 ? 'error.main' : disk.avgUtilPct >= 60 ? 'warning.main' : 'success.main';
          return (
            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader
                  avatar={<StorageIcon color="primary" />}
                  title="Disk Utilization"
                  titleTypographyProps={{ variant: 'h6' }}
                  subheader="avg across disks · last sample"
                />
                <CardContent>
                  <Box textAlign="center">
                    <Typography variant="h5" sx={{ color: utilColor }}>
                      {disk.avgUtilPct.toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">busy</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })()}

        {/* Hardware info */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              avatar={<UpdateIcon color="primary" />}
              title="Hardware"
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent sx={{ pt: 0 }}>
              {hardware.isLoading && <Skeleton variant="rectangular" height={80} />}
              {hardware.isError && (
                <Alert severity="warning" sx={{ mt: 1 }}>Hardware info unavailable</Alert>
              )}
              {hardware.data && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="text.secondary" display="block">CPU</Typography>
                    <Typography variant="body2">{hardware.data.cpu.model}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {hardware.data.cpu.cores} logical cores (
                      {hardware.data.cpu.physical_cores} physical) ·{' '}
                      {hardware.data.cpu.speed_ghz} GHz
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary" display="block">RAM (total)</Typography>
                    <Typography variant="body2">{fmtBytes(hardware.data.ram_total)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Network Interfaces
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                      {hardware.data.network_interfaces.map((n) => (
                        <Chip
                          key={n.name}
                          size="small"
                          label={n.ip4 ? `${n.name}: ${n.ip4}` : n.name}
                          variant="outlined"
                          title={`MAC: ${n.mac} · ${n.type}${n.speed_mbps ? ` · ${n.speed_mbps} Mbps` : ''}`}
                        />
                      ))}
                    </Stack>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <DiskBreakdownDrawer
        open={!!breakdownMount}
        mountPoint={breakdownMount ?? '/'}
        onClose={() => setBreakdownMount(null)}
      />

      <SystemUpdateDialog
        open={updateOpen}
        onClose={() => setUpdateOpen(false)}
        packages={updates.data?.packages ?? []}
      />
    </Box>
  );
}
